/**
 * MoveContentConsole — staff tool to relocate an asset or playlist (by id,
 * from ANY customer account) into a fixed internal testing account's folder,
 * for inspection/repro. True move: the content leaves its source account.
 *
 * Rendered as a card inside AccountsConsole.tsx (not its own OptiHub tab) —
 * layout matches AccountsConsole's AccountActionCard (stacked full-width
 * fields, single submit button).
 *
 * The backend (staff.service.ts `staffMoveContentToTestAccount`) BLOCKS the
 * move if the content is actively assigned to a device, referenced by a
 * schedule item, or nested inside another playlist in its source account —
 * this form surfaces that block message verbatim so the operator knows
 * exactly what to unassign first.
 */
import { useState } from 'react';
import { Button, Input, Label } from '@staff-app/ui';
import { useMoveContent } from '../hooks/useMoveContentConsole';

// Operator enters a bare folder NAME (no "/") — the backend
// (staff.service.ts::staffMoveContentToTestAccount) prepends the leading "/"
// when it builds the actual folder path.
const DEFAULT_FOLDER_NAME = 'CS - Folder';

// 'warn' = the SCIO move succeeded but the Power BI second-database leg
// (powerBIAssets.accountId) did not. The asset HAS moved; it just won't open in
// the msbi editor until the rewrite is finished by hand — an outcome the operator
// must not read as a plain success.
type Msg = { kind: 'ok' | 'warn' | 'err'; text: string } | null;

const MSG_CLASS: Record<'ok' | 'warn' | 'err', string> = {
  ok: 'text-xs text-muted-foreground',
  warn: 'text-xs text-warning',
  err: 'text-xs text-destructive',
};

export function MoveContentConsole() {
  const [contentId, setContentId] = useState('');
  const [folderName, setFolderName] = useState(DEFAULT_FOLDER_NAME);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<Msg>(null);
  const [moveContent] = useMoveContent();

  const valid = contentId.trim().length > 0 && folderName.trim().length > 0;

  const handleSubmit = async () => {
    setBusy(true);
    setMsg(null);
    try {
      const r = (
        await moveContent({
          variables: { contentId: contentId.trim(), folderPath: folderName.trim() },
        })
      ).data!.staffMoveContent;
      // Prefer the owner email; fall back to the account id when an account
      // has no owner on record.
      const from = r.oldOwnerEmail || r.oldAccountId;
      const to = r.newOwnerEmail || r.newAccountId;
      const moved = `Moved ${r.contentType} ${r.contentId} from ${from} to ${to} (${r.newPath}).`;
      // smallappSynced is null for non-Power-BI content (nothing to sync) and false
      // when the powerBIAssets rewrite was skipped or failed — that half-state has to
      // read as a warning, not a success.
      setMsg({
        kind: r.smallappSynced === false ? 'warn' : 'ok',
        text: r.smallappMessage ? `${moved} ${r.smallappMessage}` : moved,
      });
    } catch (e) {
      setMsg({ kind: 'err', text: (e as Error)?.message ?? 'Failed' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="flex flex-col gap-3 rounded-lg border bg-card p-4 shadow-sm"
      data-testid="accounts-card-move-content"
    >
      <div className="flex flex-col gap-1">
        <h3 className="text-sm font-semibold text-foreground">{'Move Content'}</h3>
        <p className="text-xs text-muted-foreground">
          {
            'Relocate an asset or playlist (from any customer account) into the internal testing account for inspection. This is a true move — the content leaves its source account.'
          }
        </p>
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="move-content-id" className="text-xs">
          {'Content ID'}
        </Label>
        <Input
          id="move-content-id"
          value={contentId}
          onChange={(e) => setContentId(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && valid && !busy) handleSubmit();
          }}
          placeholder="Asset or playlist id"
          data-testid="move-content-id-input"
        />
        <p className="text-[11px] text-muted-foreground">
          {'Auto-detected as an asset or a playlist.'}
        </p>
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="move-content-folder" className="text-xs">
          {'Folder Name'}
        </Label>
        <Input
          id="move-content-folder"
          value={folderName}
          onChange={(e) => setFolderName(e.target.value.replace(/\//g, ''))}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && valid && !busy) handleSubmit();
          }}
          placeholder={DEFAULT_FOLDER_NAME}
          data-testid="move-content-folder-input"
        />
        <p className="text-[11px] text-muted-foreground">
          {`Defaults to "${DEFAULT_FOLDER_NAME}" — created under the account root if it doesn't already exist.`}
        </p>
      </div>

      <Button
        onClick={handleSubmit}
        disabled={!valid || busy}
        className="w-fit cursor-pointer"
        data-testid="move-content-submit"
      >
        {busy ? 'Moving...' : 'Move Content'}
      </Button>

      {msg ? (
        <p className={MSG_CLASS[msg.kind]} data-testid="move-content-result">
          {msg.text}
        </p>
      ) : null}
    </div>
  );
}
